import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import Loader from './../loader/Loader'
import { Auth } from 'aws-amplify'
import './Login.css'
import { GpAuthShell, GpButton, GpFormField, Icon } from '../../components/gp'

const iconBtn = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--gp-ink-mute)',
    display: 'inline-flex',
    padding: 4,
}

const linkStyle = {
    color: 'var(--gp-brand)',
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer',
}

const successText = {
    font: 'var(--gp-caption)',
    color: 'var(--gp-brand)',
    marginTop: 6,
}

const BackArrow = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6" />
    </svg>
)

const ResetPassword = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordNotSafe, setPasswordNotSafe] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [waitingForCode, setWaitingForCode] = useState(false)
    const [invalidEmail, setInvalidEmail] = useState(false)
    const [code, setCode] = useState('')
    const [invalidCode, setInvalidCode] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)

    const history = useHistory()

    const goToSignUp = () => history.push('/sign-up')
    const goToLogin = () => history.push('/sign-in')

    const handleReset = () => {
        setIsLoading(true)
        Auth.forgotPassword(email)
            .then(() => {
                setWaitingForCode(true)
                setIsLoading(false)
            })
            .catch((err) => {
                setIsLoading(false)
                if (err.toString().includes('Invalid email')) {
                    setInvalidEmail(true)
                }
            })
    }

    const confirmReset = (e) => {
        e.preventDefault()
        setIsLoading(true)
        Auth.forgotPasswordSubmit(email, code, password)
            .then(() => {
                setIsLoading(false)
                setShowSuccess(true)
                setWaitingForCode(false)
                setEmail('')
                setPassword('')
                setCode('')
                setPasswordNotSafe(false)
                setTimeout(() => history.push('/sign-in'), 2000)
            })
            .catch((err) => {
                setShowSuccess(false)
                if (err.code === 'CodeMismatchException') {
                    setInvalidCode(true)
                    setPasswordNotSafe(false)
                } else if (err.code === 'InvalidPasswordException') {
                    setPasswordNotSafe(true)
                    setInvalidCode(false)
                }
            })
            .finally(() => setIsLoading(false))
    }

    const resendCode = () => {
        Auth.resendSignUp(email)
            .then(() => alert('Code resent successfully'))
            .catch(() => {})
    }

    useEffect(() => {
        document.querySelector('body').scrollTo(0, 0)
    }, [])

    return (
        <>
            {isLoading && <Loader />}
            <GpAuthShell>
                <span
                    onClick={goToLogin}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        textDecoration: 'none',
                        marginBottom: 18,
                        cursor: 'pointer',
                    }}
                >
                    <BackArrow />
                    Back to sign in
                </span>

                <h2
                    style={{
                        font: '500 32px/1.1 var(--gp-font)',
                        letterSpacing: -0.5,
                        margin: '0 0 6px',
                        color: 'var(--gp-ink)',
                    }}
                >
                    {waitingForCode ? 'Set a new password' : 'Forgot password'}
                </h2>
                <p
                    style={{
                        font: 'var(--gp-body)',
                        color: 'var(--gp-ink-mute)',
                        margin: '0 0 24px',
                    }}
                >
                    {waitingForCode ? (
                        <>
                            Enter the 6-digit code we sent to <strong>{email}</strong> and
                            choose a new password.
                        </>
                    ) : (
                        "Enter the email on your account and we'll send you a one-time code."
                    )}
                </p>

                {showSuccess && (
                    <div style={{ ...successText, marginBottom: 14 }}>
                        Password reset. Redirecting you to sign in…
                    </div>
                )}

                {!waitingForCode && !showSuccess && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <GpFormField
                            label="Email"
                            icon="mail"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            autoCapitalize="none"
                            autoComplete="email"
                            error={invalidEmail ? 'Invalid email.' : null}
                        />

                        <div style={{ marginTop: 6 }}>
                            <GpButton
                                variant="primary"
                                onClick={handleReset}
                                disabled={!email || isLoading}
                                style={{ width: '100%' }}
                            >
                                Send reset code
                            </GpButton>
                        </div>

                        <div
                            style={{
                                margin: '24px 0 0',
                                height: 1,
                                background:
                                    'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
                            }}
                        />

                        <div
                            style={{
                                marginTop: 18,
                                font: 'var(--gp-body)',
                                color: 'var(--gp-ink-mute)',
                                textAlign: 'center',
                            }}
                        >
                            Don't have an account?{' '}
                            <span onClick={goToSignUp} style={linkStyle}>
                                Sign up
                            </span>
                        </div>
                    </div>
                )}

                {waitingForCode && (
                    <form onSubmit={confirmReset} autoComplete="off">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <GpFormField
                                label="Verification code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder="Enter 6-digit code"
                                inputMode="numeric"
                                autoFocus
                                inputStyle={{
                                    font: '500 16px/1 var(--gp-mono)',
                                    letterSpacing: 2,
                                }}
                                error={invalidCode ? 'Invalid code.' : null}
                            />

                            <GpFormField
                                label="New password"
                                icon="lock"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                autoComplete="new-password"
                                trailing={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        style={iconBtn}
                                    >
                                        <Icon
                                            name={showPassword ? 'eyeOff' : 'eye'}
                                            size={18}
                                            stroke={1.75}
                                        />
                                    </button>
                                }
                                error={
                                    passwordNotSafe
                                        ? 'Password needs upper-case, number, and a symbol.'
                                        : null
                                }
                                helpText="At least 8 characters with a number and a symbol."
                            />

                            <GpButton
                                variant="primary"
                                onClick={confirmReset}
                                disabled={!code || !password || isLoading}
                                style={{ width: '100%', marginTop: 4 }}
                            >
                                Confirm new password
                            </GpButton>

                            <div
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    textAlign: 'center',
                                }}
                            >
                                Didn't get a code?{' '}
                                <span onClick={resendCode} style={linkStyle}>
                                    Resend
                                </span>
                            </div>
                        </div>
                    </form>
                )}
            </GpAuthShell>
        </>
    )
}

export default ResetPassword
